import * as _ from 'lodash';
import uuid from 'uuid/v4';
import * as types from './ACTypes';
import * as cnsts from '../AutocodeConstants';
import AutocodeLabelsGenerator, {
  ACStandardLabels,
} from '../AutocodeLabelsGenerator';
import AutocodeExpressionListGenerator from '../AutocodeExpressionListGenerator';
import { combineAllChildren } from '../AutocodeFunctions';

// ****************************************************************************
/// used for comparing paths commonality based on path and template type
const pathPrefix = node =>
  _.join(_.dropRight(_.split(node.data.textPath, '.'), 1), '.');

// ****************************************************************************
const returnSorted = nodes => {
  const sortedNodes = _.sortBy(nodes, c => {
    switch (c.type) {
      default:
        c.p = '2';
        return c.p;
      case cnsts.AC_NODETYPE_DATAPOINT:
        c.p = pathPrefix(c);
        return c.p;
      // case cnsts.AC_NODETYPE_DEFINITION:
      //   c.p = '1';
      //   return c.p;
      // case cnsts.AC_NODETYPE_OPERATOR:
      // case cnsts.AC_NODETYPE_FUNC:
      //   c.p = '2';
      //   return c.p;
    }
  });

  return sortedNodes;
};

// // ****************************************************************************
// /// show children as Question (Answer or Answer or Answer)
// const combineAllChildren = node => {
//   if (node.type !== cnsts.AC_NODETYPE_OPERATOR) return false;

//   let nodePath = '0';
//   let match = true;

//   const checkIfPathsMatch = node => {
//     const pathPrefix = node => {
//       return _.join(_.dropRight(_.split(node.data.textPath, '.'), 1), '.');
//     };

//     node.children.forEach(n => {
//       if (n.type === cnsts.AC_NODETYPE_OPERATOR) {
//         match =
//           match &&
//           node.data.type.symbol === n.data.type.symbol &&
// symbols below much match the parent symbol (AND, OR, etc)
//           checkIfPathsMatch(n); // category/subcat/question must match
//       } else if (n.type !== cnsts.AC_NODETYPE_DATAPOINT) {
//         // definition?
//         match = false;
//       } else if (match === true) {
//         // is AC_NODETYPE_DATAPOINT
//         nodePath = nodePath === '0' ? pathPrefix(n) : nodePath;
//         match = nodePath === pathPrefix(n);
//       }
//     });
//     return match;
//   };

//   return checkIfPathsMatch(node);
// };

// ****************************************************************************
export const wrapNode = (type, data) => {
  const { toLabelString, toDefString } = AutocodeLabelsGenerator(
    ACStandardLabels,
    null
  );
  const { buildObjectsAddedArray } = AutocodeExpressionListGenerator();
  const node = {
    type,
    id: uuid(), // unique id for each
    selected: false,
    data,
    children: [],
    useLabelOverride: false,
    labelOverride: '',
    group: 0, // used for bulk join
    rootnodeChild: cnsts.AC_NODETYPE_ROOT, // hangs off root node
    // combine, // are the node's text items combined 'text (x OR y or z)'
    combine() {
      return combineAllChildren(this);
    },
    toLabelString(pts) {
      return toLabelString(this, pts);
    },
    toExpressionString() {
      return toDefString(this);
    },
    toExpressionArray(pts) {
      return buildObjectsAddedArray(this, pts);
    },
  };

  return node;
};

// ****************************************************************************
export const wrapNodes = (type, nodes) => _.map(nodes, n => wrapNode(type, n));

// ****************************************************************************
/// recurse through node tree and update Ids
const refreshIdsDeep = node => {
  const doRefresh = newNode => {
    newNode.id = uuid();
    if (newNode.children.length > 0) {
      newNode.children.forEach(n => doRefresh(n));
    }
  };
  return doRefresh(node);
};

// ****************************************************************************
const findNode = (nodeId, treeNode) => {
  let node = null;
  if (!treeNode) return null;

  if (treeNode.id === nodeId) {
    return treeNode;
  }

  if (treeNode.children.length > 0) {
    _.forEach(treeNode.children, child => {
      node = findNode(nodeId, child);
    });
  }

  return node;
};

// ****************************************************************************
/// set an override label (really just for toplevelnodes)
const setOverrideLabelById = (payload, state) => {
  const { nodeId, label, useOverride } = payload;

  const newState = _.cloneDeep(state);

  const node = findNode(nodeId, newState.rootNode);
  if (node) {
    node.labelOverride = label;
    node.useLabelOverride = useOverride;
  }

  return { ...newState };
};

// ****************************************************************************
/// create a list of all operators
const updateSelectedOperatorList = state => {
  const newState = _.cloneDeep(state);
  newState.topLevelOpNodes = [];
  newState.allOpNodes = [];

  // clear rootnode flag
  const drilldownAll = (node, ptype) => {
    node.rootnodeChild = ptype;
    node.children.forEach(n => drilldownAll(n, node.type));
  };

  const drilldownAllOps = node => {
    if (node.type === cnsts.AC_NODETYPE_OPERATOR) {
      newState.allOpNodes.push(node);
      node.children.forEach(n => drilldownAllOps(n));
    }
  };

  const drilldownTopsOps = node => {
    if (node.type === cnsts.AC_NODETYPE_OPERATOR) {
      const test = node.combine();
      if (!test || node.rootnodeChild === cnsts.AC_NODETYPE_ROOT) {
        newState.topLevelOpNodes.push(node);
        node.children.forEach(n => drilldownTopsOps(n));
      }
    }
  };

  const setAllChildrenAsTops = node => {
    if (node.type === cnsts.AC_NODETYPE_OPERATOR) {
      newState.topLevelOpNodes.push(node);
      node.children.forEach(n => setAllChildrenAsTops(n));
    }
  };

  newState.rootNode.children.forEach(n => {
    drilldownAll(n, newState.rootNode.type);
    n.rootnodeChild = cnsts.AC_NODETYPE_ROOT;
  });

  newState.rootNode.children
    .filter(n => n.type === cnsts.AC_NODETYPE_OPERATOR)
    .forEach(n => {
      drilldownAllOps(n);

      // if node is top level and is combine then all operators are top level
      if (n.combine() && n.rootnodeChild === cnsts.AC_NODETYPE_ROOT) {
        setAllChildrenAsTops(n);
      } else {
        drilldownTopsOps(n);
      }
    });
  return { ...newState };
};

// ****************************************************************************
/// inserts nodes as individuals
const insertNodes = (parentNodeID, nodes, insertionPos, state) => {
  // position
  if (!nodes) return state;

  const newState = _.cloneDeep(state);

  // find the path in the heirarch and insert there
  const insertionNode = findNode(parentNodeID, newState.rootNode);
  if (insertionNode) {
    // _.forEach(nodes, node => {
    //   insertionNode.children.push(node);
    // });
    insertionNode.children = [...insertionNode.children, ...nodes];
  }

  return { ...newState };
};

// ****************************************************************************
/// search the tree and remove the node and all children
const removeNodesByIds = (payload, state) => {
  const { nodeIds } = payload;

  const newState = _.cloneDeep(state);

  const rn = (nodes, nodeId) => {
    if (nodes) {
      if (_.find(newState.rootNode.children, n => n.id === nodeId)) {
        _.remove(newState.rootNode.children, n => n.id === nodeId);
      } else {
        nodes.forEach(node => rn(node.children, nodeId));
      }
    }
  };

  nodeIds.forEach(nodeId => {
    rn(newState.rootNode.children, nodeId);
  });

  return { ...newState };
};

// ****************************************************************************
const clearSelectionBox = state => {
  // clear children from the list
  const newState = _.cloneDeep(state);

  newState.rootNode.children = [];

  return { ...newState };
};

// ****************************************************************************
/// top level nodes from top level
const removeSelectedNodes = state => {
  const newState = _.cloneDeep(state);
  _.remove(newState.rootNode.children, n => n.selected === true);

  return { ...newState };
};

// ****************************************************************************
/// remove all nodes
const removeAllNodes = state => {
  const newState = _.cloneDeep(state);
  newState.rootNode.children = [];
  return { ...newState };
};

// ****************************************************************************
/// insert a datapoint datapoint
const insertLeafNode = (payload, state) => {
  const { parentNodeID, nodes, insertionPos } = payload;
  return insertNodes(parentNodeID, nodes, insertionPos, state);
};

// ****************************************************************************
/// insert a function node
const insertFunctionNodes = (payload, state) => {
  const { parentNodeID, nodes, insertionPos } = payload;

  const newState = removeSelectedNodes(_.cloneDeep(state));
  return insertNodes(parentNodeID, nodes, insertionPos, newState);
};

// ****************************************************************************
/// change the function of a node
/// namesOnly keeps all other func data
const changeFunctionOfSelectedNodes = (payload, state) => {
  const { functionName, isSelected, editable, params } = payload;
  const newState = _.cloneDeep(state);

  const selectedFuncs = _.filter(
    newState.rootNode.children,
    n => n.type === cnsts.AC_NODETYPE_FUNC && n.data.selected === true
  );
  selectedFuncs.forEach(n => {
    n.data = {
      funcName: functionName,
      selected: isSelected,
      editable,
      params,
    };
  });
  return { ...newState };
};

// ****************************************************************************
/// finds a function node by it's ID, pulls out all children to the top level
const removeFunctionById = (payload, state) => {
  const { nodeId } = payload;
  const newState = _.cloneDeep(state);

  const node = _.filter(newState.rootNode.children, n => n.id === nodeId);
  if (node && node.length > 0) {
    // _.remove(newState.rootNode.children, node);
    const latestState = removeNodesByIds({ nodeIds: [nodeId] }, newState);
    return {
      ...insertNodes(cnsts.AC_ROOT_NODE_ID, node[0].children, -1, latestState),
    };
  }

  return { ...state };
};

// ****************************************************************************
const insertDefinitionNodes = (payload, state) => {
  const { parentNodeID, nodes, insertionPos } = payload;
  return insertNodes(parentNodeID, nodes, insertionPos, state);
};

// ****************************************************************************
/* eslint-disable */
const createOperator = (nodeLeft, nodeRight, operatorSelected, combine) => {
  const topNode = wrapNode(cnsts.AC_NODETYPE_OPERATOR, operatorSelected);
  topNode.data = { selected: false, type: operatorSelected };
  topNode.children.push(nodeRight);
  topNode.children.push(nodeLeft);
  return topNode;
};

// ****************************************************************************
/// insert an operator node
/// assumption is the cat/subcat/question match
const insertOperatorNode = (payload, state) => {
  const {
    parentNodeID,
    nodes,
    operatorSelected,
    combine,
    insertionPos,
    setAllAsTops,
  } = payload;

  if (nodes.length < 2) return { ...state }; // must be at least 2
  const hNodes = _.cloneDeep(nodes);

  let topNode = null;
  while (hNodes.length > 0) {
    if (topNode === null) {
      const pulled = _.pullAt(hNodes, [0, 1]);
      // so that order dont get change
      topNode = createOperator(pulled[1], pulled[0], operatorSelected, combine);
    } else {
      const pulled = _.pullAt(hNodes, [0]);
      topNode = createOperator(
        pulled[0],
        topNode,
        operatorSelected,
        combine,
        setAllAsTops
      );
    }
  }

  return insertNodes(parentNodeID, [topNode], insertionPos, state);
};

// ****************************************************************************
/// toggle selected nodes on top level
const setNodeSelectionById = (payload, state) => {
  const { nodeId, selected } = payload;
  const newState = _.cloneDeep(state);

  const node = _.find(newState.rootNode.children, n => n.id === nodeId);
  if (node) {
    node.selected = selected;
  }

  return { ...newState };
};

// ****************************************************************************
/// select all nodes at the top level
const selectAllNodesByType = (payload, state) => {
  const { selected, deep, type } = payload;

  const selectAllDeep = (node, deep, selected) => {
    node.children.forEach(n => {
      if (n.type === type) {
        n.selected = selected;
        if (n.children.length > 0 && deep) {
          selectAllDeep(n, deep, selected);
        }
      }
    });
  };

  const newState = _.cloneDeep(state);
  selectAllDeep(newState.rootNode, deep, selected);

  return { ...newState };
};

// ****************************************************************************
/// select all nodes at the top level
const selectAllNodes = (payload, state) => {
  const { selected, deep } = payload;

  const selectAllDeep = (node, deep, selected) => {
    node.children.forEach(n => {
      n.selected = selected;
      if (n.children.length > 0 && deep) {
        selectAllDeep(n, deep, selected);
      }
    });
  };

  const newState = _.cloneDeep(state);
  selectAllDeep(newState.rootNode, deep, selected);

  return { ...newState };
};

// ****************************************************************************
const changeSelectedOperators = (payload, state) => {
  const { newOperator, isSelected } = payload;

  const updateSelected = (node, op) => {
    node.children.forEach(n => {
      if (n.type === cnsts.AC_NODETYPE_OPERATOR && n.data.selected) {
        n.data = { selected: isSelected, type: newOperator };
      }
      updateSelected(n, op);
    });
  };

  const newState = _.cloneDeep(state);
  updateSelected(newState.rootNode, newOperator);

  return { ...newState };
};

// ****************************************************************************
const selectAllFunctions = (payload, state) => {
  const { isSelected } = payload;

  const updateSelected = (node, op) => {
    node.children.forEach(n => {
      if (n.type === cnsts.AC_NODETYPE_FUNC) {
        n.data = { ...n.data, selected: isSelected };
      }
      updateSelected(n, op);
    });
  };

  const newState = _.cloneDeep(state);
  updateSelected(newState.rootNode);

  return { ...newState };
};

// ****************************************************************************
/// toggle selected nodes on top level
const selectFunctionById = (payload, state) => {
  const { nodeId, selected } = payload;
  const newState = _.cloneDeep(state);

  const node = _.find(newState.rootNode.children, n => n.id === nodeId);
  if (node) {
    node.data.selected = selected;
  }

  return { ...newState };
};

// ****************************************************************************
/// Set selected Label type

const setSelectedLabelType = (payload, state) => {
  const { labelObj } = payload;
  const newState = _.cloneDeep(state);

  newState.partsToShow = labelObj;

  return { ...newState };
};

// ****************************************************************************
const selectAllOperators = (payload, state) => {
  const { newOperator, isSelected } = payload;

  const updateSelected = (node, op) => {
    node.children.forEach(n => {
      if (n.type === cnsts.AC_NODETYPE_OPERATOR) {
        n.data = { ...n.data, selected: isSelected };
      }
      updateSelected(n, op);
    });
  };

  const newState = _.cloneDeep(state);
  updateSelected(newState.rootNode, newOperator);

  return { ...newState };
};

const storeDefinitionNodess = (payload, state) => {
  const newState = _.cloneDeep(state);
  newState.definitionNode = payload;
  return { ...newState };
}

const setBackToRootNode = (payload, state) => {
  const newState = _.cloneDeep(state);
  newState.rootNode = state.definitionNode;
  return { ...newState };
}

// ****************************************************************************
const changeSelectedOperatorById = (payload, state) => {
  const { newOperator, nodeId, isSelected } = payload;

  const updateSelected = (node, op) => {
    node.children.forEach(n => {
      if (n.type === cnsts.AC_NODETYPE_OPERATOR && n.id === nodeId) {
        n.data = { ...n.data, selected: isSelected, type: newOperator };
      }
      updateSelected(n, op);
    });
  };

  const newState = _.cloneDeep(state);
  updateSelected(newState.rootNode, newOperator);

  return { ...newState };
};

// ****************************************************************************
/// combines nodes with an operator that are already in the selected list
const mergeSelectedNodes = (payload, state) => {
  const { operatorSelected, removeSourceNodes, insertionPos } = payload;
  const newState = _.cloneDeep(state);

  // check the path not including the answer path
  let nodePath = '0';
  let match = true;
  const checkIfPathsMatch = nodes => {
    if (nodes.length === 0) return true;

    nodes.forEach(n => {
      if (n.type === cnsts.AC_NODETYPE_OPERATOR) {
        if (n.combine() === false) match = false;
        else {
          match = match && checkIfPathsMatch(n.children);
        }
      } else if (n.type !== cnsts.AC_NODETYPE_DATAPOINT) {
        // definition?
        match = false;
      } else if (match === true) {
        // is AC_NODETYPE_DATAPOINT
        nodePath = nodePath === '0' ? pathPrefix(n) : nodePath;
        match = nodePath === pathPrefix(n);
      }
    });
    return match;
  };

  const mNodes =
    operatorSelected.label !== 'AND NOT'
      ? returnSorted(_.filter(newState.rootNode.children, c => c.selected))
      : _.filter(newState.rootNode.children, c => c.selected);

  const grouped = _.groupBy(mNodes, c => c.p);
  const groupArrays = _.map(_.toPairs(grouped), x => x[1]);

  if (groupArrays.length >= 2) {
    const newNodes = [];
    groupArrays.forEach(ga => {
      if (ga && ga.length >= 2) {
        const pl = {
          parentNodeID: cnsts.AC_ROOT_NODE_ID,
          nodes: ga,
          operatorSelected,
          setAllAsTops: true,
          insertionPos,
        };

        const newState2 = _.clone(state);
        newState2.rootNode.children = [];
        const ns = insertOperatorNode(pl, newState2).rootNode.children;
        newNodes.push(ns[0]);
      } else newNodes.push(ga[0]);
    });

    if (newNodes.length >= 2) {
      const pl = {
        parentNodeID: cnsts.AC_ROOT_NODE_ID,
        nodes: newNodes,
        operatorSelected,
        setAllAsTops: true,
        insertionPos,
      };

      const newerState = insertOperatorNode(pl, newState);
      if (removeSourceNodes)
        _.remove(newerState.rootNode.children, c => c.selected);
      else
        newerState.rootNode.children.forEach(x => {
          x.selected = false;
          x.id = uuid(); // new ID in case this is a copy
        });
      return { ...newerState };
    }
  } else if (mNodes && mNodes.length >= 2) {
    checkIfPathsMatch(mNodes);

    const pl = {
      parentNodeID: cnsts.AC_ROOT_NODE_ID,
      nodes: mNodes,
      operatorSelected,
      setAllAsTops: true,
      insertionPos,
    };

    const newerState = insertOperatorNode(pl, newState);
    if (removeSourceNodes)
      _.remove(newerState.rootNode.children, c => c.selected);
    else
      newerState.rootNode.children.forEach(x => {
        x.selected = false;
        x.id = uuid(); // new ID in case this is a copy
      });
    return { ...newerState };
  }

  return { ...state };
};

// ****************************************************************************
/// combine selected into NET ANY
const combineSelectedIntoNetAny = (payload, state) => {
  const { operatorSelected, insertionPos } = payload;
  const newState = _.cloneDeep(state);
  const selectedNodes = newState.rootNode.children.filter(n => n.selected);

  // break out all the nodes at the top level and under any NET ANY Funcs
  const nodesHolder = [];
  selectedNodes.forEach(node => {
    switch (node.type) {
      default:
        break; // this is really an error
      case cnsts.AC_NODETYPE_OPERATOR: // these two types just get added to the list
      case cnsts.AC_NODETYPE_DATAPOINT: {
        nodesHolder.push(node);
        break;
      }
      case cnsts.AC_NODETYPE_FUNC: {
        if (
          _.lowerCase(node.data.funcName) === 'net any' &&
          ((node.data.params && node.data.params.operator) ||
            node.data.operator)
        ) {
          // node.children.forEach(n => nodesHolder.push(n));
          // children is treated as group and expression combine
          const pl = {
            parentNodeID: cnsts.AC_ROOT_NODE_ID,
            nodes: node.children,
            operatorSelected: node.data.params
              ? node.data.params.operator
              : node.data.operator,
            setAllAsTops: true,
            insertionPos,
          };

          const newState2 = _.clone(state);
          newState2.rootNode.children = [];
          const ns = insertOperatorNode(pl, newState2).rootNode.children;
          nodesHolder.push(ns[0]);
        } else {
          nodesHolder.push(node.children[0]);
        }
        // else THIS IS AN ERROR
        break;
      }
    }
  });

  // build the new NET ANY
  const funct = {
    editable: true,
    funcName: 'net any',
    selected: false,
    operator: operatorSelected,
  };

  const node = wrapNode(cnsts.AC_NODETYPE_FUNC, funct);
  node.children = _.map(_.cloneDeep(nodesHolder), n => {
    n.id = uuid();
    n.selected = false;
    return n;
  });

  const payload2 = {
    parentNodeID: cnsts.AC_ROOT_NODE_ID,
    nodes: [node],
    insertionPos: -1,
  };

  const newState2 = insertFunctionNodes(payload2, newState);
  // delete selected
  return removeSelectedNodes(newState2);
};

// ****************************************************************************
// ****************************************************************************
/// split selected node into separate nodes
const splitSelectedNodes = state => {
  const sn = (node, splitNodes) => {
    if (node.type !== cnsts.AC_NODETYPE_OPERATOR) {
      node.id = uuid();
      splitNodes.push(node);
    } else {
      node.children.forEach(c => {
        sn(c, splitNodes);
      });
    }
  };

  const newState = _.cloneDeep(state);

  const rootNodes = _.filter(
    newState.rootNode.children,
    x => x.selected === true
  );
  if (rootNodes && rootNodes.length >= 1) {
    const splitNodes = [];
    rootNodes.forEach(n => {
      sn(n, splitNodes);
    });

    if (splitNodes.length > 1) {
      _.remove(newState.rootNode.children, x => x.selected === true);
      return {
        ...insertNodes(cnsts.AC_ROOT_NODE_ID, splitNodes, -1, newState),
      };
    }
  }

  return { ...state };
};

// ****************************************************************************
/// split a selected boolean node into separate nodes
/// always delete and move the sibling up on level
const splitSelectedOpNodes = state => {
  const drilldownTops = (parent, node, opNodes, orphanedNodes) => {
    if (node.type === cnsts.AC_NODETYPE_OPERATOR) {
      if (node.combine() === true) {
        node.children.forEach(n =>
          drilldownTops(node, n, opNodes, orphanedNodes)
        );
      }
      if (node.data.selected === true) {
        opNodes.push(node.children[1]);
        // parent.children = parent.children.filter(x => x.id !== node.id);
        const child = _.cloneDeep(node.children[0]);
        node.type = child.type;
        node.data = child.data;
        node.useLabelOverride = false;
        node.labelOverride = '';
        node.children = child.children;
        node.selected = false;
        node.id = uuid();
      }
    }
  };

  const newState = _.cloneDeep(state);

  const splitOffNodes = [];
  // newState.rootNode.children
  //   .filter(n => n.type === cnsts.AC_NODETYPE_OPERATOR)
  //   .forEach(n => {
  //     drilldownTops(newState.rootNode, n, splitOffNodes);
  //   });

  newState.topLevelOpNodes.forEach(n => {
    drilldownTops(newState.rootNode, n, splitOffNodes);
  });

  return splitOffNodes.length > 0
    ? { ...insertNodes(cnsts.AC_ROOT_NODE_ID, splitOffNodes, -1, newState) }
    : { ...state };
};

// ****************************************************************************
/// duplicate selected nodes
/// ids need to be updated deep into the tree
/// deselect selected and select newly created nodes
const duplicateSelectedNodes = state => {
  const newState = _.cloneDeep(state);
  const selected = _.filter(
    newState.rootNode.children,
    n => n.selected === true
  );
  selected.forEach(x => {
    x.selected = false;
  });
  newState.rootNode.children = [
    ...newState.rootNode.children,
    ..._.map(_.cloneDeep(selected), item => {
      refreshIdsDeep(item);
      item.selected = true;
      return item;
    }),
  ];

  return { ...newState };
};

// ****************************************************************************
/// copy selected nodes
const copySelectedNodes = state => {
  const newState = _.cloneDeep(state);
  newState.copiedExpressions = _.map(
    _.cloneDeep(_.filter(newState.rootNode.children, n => n.selected === true)),
    item => {
      // item.id = uuid();
      refreshIdsDeep(item);
      return item;
    }
  );
  return { ...newState };
};

// ****************************************************************************
/// paste copied nodes
const pasteCopiedNodes = state => {
  const newState = _.cloneDeep(state);
  if (newState.rootNode.children)
    newState.rootNode.children.forEach(c => {
      c.selected = false;
    });
  if (newState.copiedExpressions) {
    newState.rootNode.children = [
      ...newState.rootNode.children,
      ...newState.copiedExpressions,
    ];
  }
  newState.copiedExpressions = [];
  return { ...newState };
};

// ****************************************************************************
/// group up nodes for merging (only works with dictionary entries)
const setupBulkJoin = state => {
  const newState = _.cloneDeep(state);
  const sortedChildren = returnSorted(newState.rootNode.children);

  let lastPath = '';
  let groupNo = 0; // skips over the first one used by select
  sortedChildren.forEach(c => {
    if (lastPath !== c.p) {
      groupNo += 1;
      lastPath = c.p;
    }
    c.group = groupNo;
  });

  return { ...newState };
};

// set back to def nodes...
const setDefinitionNodes = (payload, state) => {
  return { ...newState };
};


// ****************************************************************************
/// ungroup nodes for merging
const cancelBulkJoinSetup = state => {
  const newState = _.cloneDeep(state);
  newState.rootNode.children.forEach(x => {
    x.group = 0;
  });

  return { ...newState };
};

// ****************************************************************************
/// group up nodes for merging (only works with dictionary entries)
const mergeBulkJoin = (payload, state) => {
  const { operatorSelected, removeSourceNodes, insertionPos } = payload;

  let newState = _.cloneDeep(state);
  const groupedChildren = _.groupBy(newState.rootNode.children, c => {
    switch (c.type) {
      default:
        return '0';
      case cnsts.AC_NODETYPE_DATAPOINT:
        return pathPrefix(c);
      case cnsts.AC_NODETYPE_DEFINITION:
        return '1';
      case cnsts.AC_NODETYPE_OPERATOR:
      case cnsts.AC_NODETYPE_FUNC:
        return '2';
    }
  });

  const groupArrays = _.map(_.toPairs(groupedChildren), x => x[1]);
  const numGroups = groupArrays.length;
  if (numGroups > 1) {
    const offsets = _.fill(Array(numGroups), 0);

    const addToMergedList = () => {
      const IncrementOffsets = () => {
        const incAndCheck = curOff => {
          const pos = offsets[curOff];
          if (pos >= groupArrays[curOff].length - 1 && curOff !== 0) {
            offsets[curOff] = 0;
            return false;
          }
          /* else */
          offsets[curOff] += 1;
          return true;
        };

        let index = groupArrays.length - 1;
        while (index >= 0 && incAndCheck(index) === false) {
          index -= 1;
        }

        return offsets[0] < groupArrays[0].length;
      };

      const AddToState = (mArray, nState) => {
        mArray.forEach(n => {
          n.id = uuid();
          n.selected = false;
        });

        const payload2 = {
          parentNodeID: cnsts.AC_ROOT_NODE_ID,
          nodes: mArray,
          operatorSelected,
          setAllAsTops: true,
          insertionPos,
        };

        return insertOperatorNode(payload2, nState);
      };

      do {
        const mergedArray = [];
        offsets.forEach((i, x) => {
          mergedArray.push(groupArrays[x][i]);
        });
        newState = AddToState(mergedArray, newState);
      } while (IncrementOffsets());
    };

    addToMergedList();

    if (removeSourceNodes)
      _.remove(newState.rootNode.children, c => c.selected);
  } else if (numGroups === 1) {
    return mergeSelectedNodes(payload, state);
  }

  return { ...newState };
};

// ****************************************************************************
/// topNode is the tree of nodes to look in
// template num is the number to match
// operatorType is AND, OR, NOT OR
const selectAllTemplateOperatorsByType = (payload, state) => {
  const { templateNum, operatorType } = payload;

  const matchTemplate = (node, tNum, opType, isMatched) => {
    if (!_.isEqual(node.data.type, opType)) isMatched.matched = false;
    else
      node.children.forEach(n => {
        if (n.type === cnsts.AC_NODETYPE_OPERATOR) {
          matchTemplate(n, tNum, opType, isMatched);
        } else if (
          n.type !== cnsts.AC_NODETYPE_DATAPOINT ||
          n.data.template !== tNum
        ) {
          isMatched.matched = false;
        }
      });
  };

  const setAllOpsToTrue = node => {
    if (_.isEqual(node.type, cnsts.AC_NODETYPE_OPERATOR)) {
      node.children
        .filter(n => n.type === cnsts.AC_NODETYPE_OPERATOR)
        .forEach(n => setAllOpsToTrue(n));
      node.data.selected = true;
    }
  };

  const newState = _.cloneDeep(state);

  newState.rootNode.children
    .filter(x => x.type === cnsts.AC_NODETYPE_OPERATOR)
    .forEach(n => {
      const isMatched = { matched: true };
      matchTemplate(n, templateNum, operatorType, isMatched);
      if (isMatched.matched) setAllOpsToTrue(n);
    });

  return { ...newState };
};

const insertOperatorNetAnyNode = (payload, state) => {
  const { functionName, params } = payload;
  const funct = {
    funcName: functionName,
    selected: false,
    editable: true,
    params,
  };
  const node = wrapNode(cnsts.AC_NODETYPE_FUNC, funct);
  node.children = _.map(_.cloneDeep(payload.nodes), n => {
    n.id = uuid();
    n.selected = false;
    return n;
  });
  payload.nodes = [node];
  return insertFunctionNodes(payload, state);
};

// for single netany
const insertNetanyLeafNode = (payload, state) => {
  const { nodes, parentNodeID, insertionPos } = payload;
  const newState = removeSelectedNodes(_.cloneDeep(state));
  return insertNodes(parentNodeID, nodes, insertionPos, newState);
};

// remove multiple netany into single netany from selectionbox
const removeNetany = (payload, state) => {
  const { nodeId } = payload;
  let newState = _.cloneDeep(state);

  const node = _.filter(newState.rootNode.children, n => n.id === nodeId);
  if (node[0].children.length > 1) {
    let newState2 = '';
    node[0].children.forEach(nod => {
      // build the new NET ANY for each node
      const funct = {
        editable: false,
        funcName: 'net any',
        selected: false,
      };
      const node = wrapNode(cnsts.AC_NODETYPE_FUNC, funct);
      node.children.push(nod);

      const payload2 = {
        parentNodeID: cnsts.AC_ROOT_NODE_ID,
        nodes: [node],
        insertionPos: -1,
      };

      newState2 = insertFunctionNodes(payload2, newState);
      newState = _.cloneDeep(newState2);
    });
    _.remove(newState2.rootNode.children, n => n.id === nodeId);

    return { ...newState2 };
  }

  return { ...newState };
};

// ****************************************************************************
// ****************************************************************************
export default (state, action) => {
  let newState;
  switch (action.type) {
    default:
      return state;
    case types.AC_CREATE_LEAF_NODE: {
      action.payload.nodes = wrapNodes(
        cnsts.AC_NODETYPE_DATAPOINT,
        action.payload.nodes
      );
      newState = insertLeafNode(action.payload, state);
      break;
    }
    case types.AC_CREATE_DEFINITION_NODE: {
      action.payload.nodes = wrapNodes(
        cnsts.AC_NODETYPE_DEFINITION,
        action.payload.nodes
      );
      newState = insertDefinitionNodes(action.payload, state);
      break;
    }
    case types.AC_CREATE_OPERATOR_NODE: {
      action.payload.nodes = wrapNodes(
        cnsts.AC_NODETYPE_DATAPOINT,
        action.payload.nodes
      );
      action.payload.setAllAsTops = false;
      newState = insertOperatorNode(action.payload, state);
      break;
    }
    case types.AC_CREATE_FUNC_NODE: {
      const funct = {
        funcName: action.payload.functionName,
        selected: action.payload.isSelected,
        editable: action.payload.editable,
        params: action.payload.params,
      };
      if (action.payload.asIndividuals === true) {
        // insert as individual functions instead of grouped
        newState = _.cloneDeep(state);
        _.forEach(action.payload.nodes, n => {
          const node = wrapNode(cnsts.AC_NODETYPE_FUNC, funct);
          node.children.push({ ...n, id: uuid(), selected: false });
          const payload = {
            parentNodeID: action.payload.parentNodeID,
            nodes: [node],
            insertionPos: action.payload.insertPos,
          };
          newState = insertFunctionNodes(payload, newState);
        });
      } else {
        const node = wrapNode(cnsts.AC_NODETYPE_FUNC, funct);
        node.children = _.map(_.cloneDeep(action.payload.nodes), n => {
          n.id = uuid();
          n.selected = false;
          return n;
        });
        action.payload.nodes = [node];
        newState = insertFunctionNodes(action.payload, state);
      }
      break;
    }
    case types.AC_MERGE_SELECTED_NODES:
      newState = mergeSelectedNodes(action.payload, state);
      break;

    case types.AC_MERGE_SELECTED_NETANY:
      newState = combineSelectedIntoNetAny(action.payload, state);
      break;
    case types.AC_SPLIT_SELECTED_NODE:
      newState = splitSelectedNodes(state);
      break;
    case types.AC_SPLIT_SELECTED_OP_NODE:
      newState = splitSelectedOpNodes(state);
      break;
    case types.AC_REMOVE_NODE_BY_ID:
      newState = removeNodesByIds(action.payload, state);
      break;
    case types.AC_CLEAR_SELECTION_BOX:
      newState = clearSelectionBox(state);
      break;
    case types.AC_REMOVE_SELECTED_NODES:
      newState = removeSelectedNodes(state);
      break;
    case types.AC_REMOVE_ALL_NODES:
      newState = removeAllNodes(state);
      break;
    case types.AC_TOGGLE_SELECTED_NODE:
      newState = setNodeSelectionById(action.payload, state);
      break;
    case types.AC_SELECT_ALL_NODES:
      newState = selectAllNodes(action.payload, state);
      break;
    case types.AC_SELECT_ALL_NODES_BY_TYPE:
      newState = selectAllNodesByType(action.payload, state);
      break;
    case types.AC_SELECT_ALL_OPERATORS:
      newState = selectAllOperators(action.payload, state);
      break;
    case types.AC_CHANGE_SELECTED_OPERATOR:
      newState = changeSelectedOperators(action.payload, state);
      break;
    case types.AC_CHANGE_SELECTED_OPERATOR_BY_ID:
      newState = changeSelectedOperatorById(action.payload, state);
      break;
    case types.AC_DUPLICATE_SELECTED_NODES:
      newState = duplicateSelectedNodes(state);
      break;
    case types.AC_COPY_SELECTED_NODES:
      newState = copySelectedNodes(state);
      break;
    case types.AC_PASTE_COPIED_NODES:
      newState = pasteCopiedNodes(state);
      break;
    case types.AC_SET_OVERRIDE_LABEL_BY_ID:
      newState = setOverrideLabelById(action.payload, state);
      break;
    case types.AC_SETUP_BULK_JOIN:
      newState = setupBulkJoin(state);
      break;
    case types.AC_CANCEL_BULK_JOIN_SETUP:
      newState = cancelBulkJoinSetup(state);
      break;
    case types.AC_MERGE_BULK_JOIN:
      newState = mergeBulkJoin(action.payload, state);
      break;
    case types.AC_REMOVE_FUNCTION_NODE_BY_ID:
      newState = removeFunctionById(action.payload, state);
      break;
    case types.AC_CHANGE_FUNC_OF_SELECTED_NODES:
      newState = changeFunctionOfSelectedNodes(action.payload, state);
      break;
    case types.AC_SELECT_ALL_FUNCTIONS:
      newState = selectAllFunctions(action.payload, state);
      break;
    case types.AC_SELECT_FUNCTION_BY_ID:
      newState = selectFunctionById(action.payload, state);
      break;
    case types.AC_SELECTED_LABEL_TYPE:
      newState = setSelectedLabelType(action.payload, state);
      break;
    case types.AC_SELECT_ALL_TEMPLATE_OPERATORS_BY_TYPE:
      newState = selectAllTemplateOperatorsByType(action.payload, state);
      break;
    case types.AC_INSERT_NODES:
      newState = insertNodes(
        action.payload.parentNodeID,
        [...action.payload.nodes],
        action.payload.insertionPos,
        state
      );
      break;
      case types.SET_DEFINITION_NODES:
        newState = storeDefinitionNodess(action.payload, state)
        break;
      case types.SET_BACK_TO_ROOTNODE:
        newState = setBackToRootNode(action.payload, state)
        break;

    case types.AC_CREATE_NETANY_LEAF_NODE: {
      action.payload.nodes = wrapNodes(
        cnsts.AC_NODETYPE_DATAPOINT,
        action.payload.nodes
      );
      const func = {
        funcName: action.payload.functionName,
        selected: action.payload.isSelected,
        editable: action.payload.editable,
        params: action.payload.params,
      };

      newState = _.cloneDeep(state);
      _.forEach(action.payload.nodes, n => {
        const node = wrapNode(cnsts.AC_NODETYPE_FUNC, func);
        node.children.push({ ...n, id: uuid(), selected: false });
        const payload = {
          parentNodeID: action.payload.parentNodeID,
          nodes: [node],
          insertionPos: action.payload.insertPos,
        };
        newState = insertNetanyLeafNode(payload, newState);
      });
      break;
    }
    case types.AC_OPERATORS_NETANY: {
      action.payload.nodes = wrapNodes(
        cnsts.AC_NODETYPE_DATAPOINT,
        action.payload.nodes
      );
      newState = insertOperatorNetAnyNode(action.payload, state);
      break;
    }
    case types.AC_REMOVE_NETANY:
      newState = removeNetany(action.payload, state);
      break;
  }

  newState = updateSelectedOperatorList(newState);

  return newState;
};
